/**
 * A aba "Exames" do cliente (#811, fase 3): os pedidos de exame e as etapas
 * de cada um.
 *
 * ── Só com a clínica ligada ──────────────────────────────────────────────
 *
 * A aba só existe com a funcionalidade "clinica" ligada; sem ela, a API
 * responde 404 e a tela nem pergunta.
 *
 * ── A trilha das etapas (16/10/2026) ─────────────────────────────────────
 *
 * As etapas viraram uma trilha na horizontal: o número cheio é a feita, o de
 * anel é a atual, o apagado é o que falta. Embaixo de cada uma, quem deu o ok
 * e quando, ou desde quando ela espera. Pedido parado há uma semana ou mais
 * ganha o aviso no título. No celular a trilha desce, uma etapa por linha.
 *
 * ── Um botão por etapa, e só o que o servidor aceita ─────────────────────
 *
 * O ok segue a ordem e só a última etapa feita se desfaz. Por isso "Dar ok"
 * aparece só na etapa atual, e "Desfazer" só na última com ok: um botão em
 * outra etapa existiria só para receber a recusa. "Editar etapas", "Editar
 * dados" e "Cancelar pedido" foram para o menu do pedido, para a trilha ser o
 * que se vê primeiro.
 *
 * ── O pedido novo num diálogo ────────────────────────────────────────────
 *
 * O cadastro tem os dados do exame e a montagem das etapas lado a lado: na
 * aba, os dois empurravam a lista de pedidos para baixo da dobra. As etapas
 * da clínica entram com um clique; a escrita fica marcada "só deste pedido".
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ErroDaApi,
  type EtapaDaClinica,
  type EtapaNovaDoExame,
  type ExameDoPaciente,
  type MudancaDeCampos,
  type ValoresDeCampos,
} from '@cliqcrm/contracts';
import {
  useApi,
  useCamposPersonalizados,
  useEtapasDaClinica,
  useExamesDoPaciente,
} from '@/shared/api/consultas';
import {
  CamposEditaveis,
  obrigatoriosVazios,
  ValoresDosCampos,
} from '@/entities/campos/campo-personalizado';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import {
  Botao,
  Campo,
  CampoArea,
  Chip,
  Dialogo,
  FiltroPilula,
  Icone,
  MenuAcoes,
  Selo,
  Situacao,
} from '@/shared/ui';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import {
  camposDoCorpo,
  carimbo,
  diasDesde,
  DIAS_PARA_PARADO,
  esperandoDesde,
  etapaAtual,
  etapasAbertas,
  etapasSugeridas,
  FORMULARIO_VAZIO,
  formularioDe,
  haDias,
  instanteCurto,
  LIMITE_DE_ETAPAS,
  mover,
  situacaoDoExame,
  textoDoOk,
  ultimaFeita,
  type FormularioDoExame,
} from '../lib/exames-do-paciente';
import './aba-dos-exames.css';

const CONFLITO = 'Outra pessoa mudou este pedido enquanto você editava. Recarregue e refaça.';

function recado(erro: unknown, generica: string): string {
  if (erro instanceof ErroDaApi && erro.codigo === 'CONFLITO_DE_VERSAO') return CONFLITO;
  return recadoDeErro(erro, { generica });
}

type Filtro = 'todos' | 'abertos' | 'concluidos' | 'cancelados';

function grupoDo(exame: ExameDoPaciente): Exclude<Filtro, 'todos'> {
  const situacao = situacaoDoExame(exame);
  if (situacao === 'cancelado') return 'cancelados';
  if (situacao === 'concluido') return 'concluidos';
  return 'abertos';
}

const NOMES_DO_FILTRO: Readonly<Record<Exclude<Filtro, 'todos'>, string>> = {
  abertos: 'Em andamento',
  concluidos: 'Concluídos',
  cancelados: 'Cancelados',
};

export function ExamesNoCliente({
  clienteId,
  podeEditar,
}: {
  clienteId: string;
  podeEditar: boolean;
}): ReactNode {
  const consulta = useExamesDoPaciente(clienteId);
  const [cadastrando, definirCadastrando] = useState(false);
  const [filtro, definirFiltro] = useState<Filtro>('todos');

  if (consulta.isPending) {
    return <Carregando forma="lista" linhas={2} rotulo="Carregando os exames" />;
  }
  if (consulta.isError) {
    return (
      <Erro
        erro={consulta.error}
        titulo="Não foi possível abrir os exames"
        aoRepetir={() => void consulta.refetch()}
      />
    );
  }

  const exames = consulta.data.itens;
  const contagem = (grupo: Exclude<Filtro, 'todos'>) =>
    exames.filter((exame) => grupoDo(exame) === grupo).length;
  const visiveis = filtro === 'todos' ? exames : exames.filter((e) => grupoDo(e) === filtro);
  const abertos = visiveis.filter((exame) => grupoDo(exame) === 'abertos');
  const fechados = visiveis.filter((exame) => grupoDo(exame) !== 'abertos');

  return (
    <div className="exames-cli">
      <div className="exames-cli__barra">
        {exames.length > 0 && (
          <div className="exames-cli__filtros" role="group" aria-label="Situação dos pedidos">
            <FiltroPilula
              ligado={filtro === 'todos'}
              aoAlternar={() => definirFiltro('todos')}
              contagem={exames.length}
            >
              Todos
            </FiltroPilula>
            {(Object.keys(NOMES_DO_FILTRO) as Exclude<Filtro, 'todos'>[])
              .filter((grupo) => contagem(grupo) > 0)
              .map((grupo) => (
                <FiltroPilula
                  key={grupo}
                  ligado={filtro === grupo}
                  aoAlternar={() => definirFiltro(filtro === grupo ? 'todos' : grupo)}
                  contagem={contagem(grupo)}
                >
                  {NOMES_DO_FILTRO[grupo]}
                </FiltroPilula>
              ))}
          </div>
        )}
        {podeEditar && (
          <span className="exames-cli__novo">
            <Botao variante="primario" onClick={() => definirCadastrando(true)}>
              <Icone nome="criar" />
              Novo pedido de exame
            </Botao>
          </span>
        )}
      </div>

      {cadastrando && (
        <CadastroDoExame clienteId={clienteId} aoFechar={() => definirCadastrando(false)} />
      )}

      {exames.length === 0 && <p className="exames-cli__vazio">Nenhum exame cadastrado.</p>}

      {abertos.length > 0 && (
        <ul className="exames-cli__lista" aria-label="Exames em andamento">
          {abertos.map((exame) => (
            <PedidoDeExame
              key={exame.id}
              clienteId={clienteId}
              exame={exame}
              podeEditar={podeEditar}
            />
          ))}
        </ul>
      )}

      {fechados.length > 0 && (
        <section className="exames-cli__fechados" aria-labelledby="exames-fechados">
          <h3 id="exames-fechados" className="exames-cli__versal">
            Fechados
          </h3>
          <ul className="exames-cli__lista exames-cli__lista--fechados">
            {fechados.map((exame) => (
              <PedidoFechado key={exame.id} exame={exame} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function PedidoDeExame({
  clienteId,
  exame,
  podeEditar,
}: {
  clienteId: string;
  exame: ExameDoPaciente;
  podeEditar: boolean;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [modo, definirModo] = useState<'ver' | 'dados' | 'etapas' | 'cancelar'>('ver');
  const camposDoPedido = useCamposPersonalizados('pedido_de_exame').data?.itens ?? [];
  const invalidar = () =>
    void cache.invalidateQueries({ queryKey: chaves.examesDoPaciente(clienteId) });
  const caminho = { id: clienteId, pedidoId: exame.id };

  const marcar = useMutation({
    mutationFn: ({ etapaId, ok }: { etapaId: string; ok: boolean }) =>
      ok
        ? api!.concluirEtapaDoExame({ caminho: { ...caminho, etapaId } })
        : api!.desfazerEtapaDoExame({ caminho: { ...caminho, etapaId } }),
    onSuccess: invalidar,
  });

  const feitas = exame.etapas.filter((etapa) => etapa.concluida_em !== null).length;
  const desde = esperandoDesde(exame);
  const dias = desde ? diasDesde(desde) : 0;

  return (
    <li className="exames-cli__item">
      <div className="exames-cli__cabeca">
        <h3 className="exames-cli__nome">{exame.exame}</h3>
        {exame.urgente && <Chip tom="atencao">Urgente</Chip>}
        {dias >= DIAS_PARA_PARADO && <Chip tom="atencao">Parado {haDias(dias)}</Chip>}
        {exame.etapas.length > 0 && (
          <Situacao texto={`${feitas} de ${exame.etapas.length} etapas`} tom="neutro" />
        )}
        {podeEditar && modo === 'ver' && (
          <span className="exames-cli__menu">
            <MenuAcoes
              rotulo={`Ações do pedido ${exame.exame}`}
              itens={[
                { rotulo: 'Editar etapas', aoClicar: () => definirModo('etapas') },
                { rotulo: 'Editar dados do pedido', aoClicar: () => definirModo('dados') },
                {
                  rotulo: 'Cancelar pedido',
                  aoClicar: () => definirModo('cancelar'),
                  perigo: true,
                  separadorAntes: true,
                },
              ]}
            />
          </span>
        )}
      </div>
      <DetalheDoPedido exame={exame} />

      {modo === 'dados' ? (
        <DadosDoExame
          prefixo={`exame-${exame.id}`}
          inicial={formularioDe(exame)}
          valores={exame.campos}
          aoSalvar={(formulario, campos) =>
            api!.editarExameDoPaciente({
              caminho,
              corpo: {
                ...camposDoCorpo(formulario),
                ...(Object.keys(campos).length > 0 ? { campos } : {}),
                versao: exame.versao,
              },
            })
          }
          aoSalvo={() => {
            definirModo('ver');
            invalidar();
          }}
          aoCancelar={() => definirModo('ver')}
        />
      ) : modo === 'etapas' ? (
        <EdicaoDasEtapas
          prefixo={`etapas-${exame.id}`}
          inicial={etapasAbertas(exame)}
          jaFeitas={feitas}
          aoSalvar={(etapas) =>
            api!.definirEtapasDoExame({ caminho, corpo: { etapas, versao: exame.versao } })
          }
          aoSalvo={() => {
            definirModo('ver');
            invalidar();
          }}
          aoCancelar={() => definirModo('ver')}
        />
      ) : (
        <Trilha
          exame={exame}
          podeEditar={podeEditar}
          ocupado={marcar.isPending}
          desde={desde}
          aoMarcar={(etapaId, ok) => marcar.mutate({ etapaId, ok })}
        />
      )}

      {exame.observacao && modo === 'ver' && (
        <p className="exames-cli__observacao">
          <Icone nome="nota" />
          <span>{exame.observacao}</span>
        </p>
      )}

      {/* Os campos personalizados do pedido (#568), na leitura. */}
      {modo === 'ver' && <ValoresDosCampos campos={camposDoPedido} valores={exame.campos} />}

      {marcar.isError && (
        <p className="exames-cli__erro" role="alert">
          {recado(marcar.error, 'Não foi possível salvar a etapa.')}
        </p>
      )}

      {modo === 'cancelar' && (
        <CancelarPedido
          clienteId={clienteId}
          exame={exame}
          aoFechar={() => definirModo('ver')}
          aoCancelado={invalidar}
        />
      )}
    </li>
  );
}

/** A linha de fatos do pedido: quando, onde, quem pediu e os códigos. */
function DetalheDoPedido({ exame }: { exame: ExameDoPaciente }): ReactNode {
  const partes = [
    `Pedido em ${new Date(exame.criado_em).toLocaleDateString('pt-BR')}`,
    exame.laboratorio,
    exame.solicitante ? `pedido por ${exame.solicitante}` : null,
    exame.codigo_do_exame ? `código ${exame.codigo_do_exame}` : null,
    exame.codigo_do_pedido ? `pedido ${exame.codigo_do_pedido}` : null,
  ].filter((parte): parte is string => parte !== null);
  return <span className="exames-cli__detalhe">{partes.join(' · ')}</span>;
}

/** As etapas na horizontal, com o ok de cada uma. */
function Trilha({
  exame,
  podeEditar,
  ocupado,
  desde,
  aoMarcar,
}: {
  exame: ExameDoPaciente;
  podeEditar: boolean;
  ocupado: boolean;
  desde: string | null;
  aoMarcar?: (etapaId: string, ok: boolean) => void;
}): ReactNode {
  const aberto = exame.cancelado_em === null;
  const atual = aberto ? etapaAtual(exame) : null;
  const ultima = aberto ? ultimaFeita(exame) : null;

  if (exame.etapas.length === 0) {
    return (
      <p className="exames-cli__nota">
        Sem etapas.{aberto && podeEditar ? ' Use "Editar etapas", no menu do pedido.' : ''}
      </p>
    );
  }

  return (
    <ol className="exames-cli__trilha" aria-label={`Etapas de ${exame.exame}`}>
      {exame.etapas.map((etapa) => {
        const feita = etapa.concluida_em !== null;
        const ehAtual = etapa.id === atual?.id;
        const estado = feita ? 'feita' : ehAtual ? 'atual' : 'aberta';
        const parado = ehAtual && desde !== null && diasDesde(desde) >= DIAS_PARA_PARADO;
        return (
          <li key={etapa.id} className="exames-cli__passo" data-estado={estado}>
            <span className="exames-cli__topo">
              <span className="exames-cli__disco" aria-hidden="true">
                {etapa.ordem}
              </span>
              <span className="exames-cli__fio" aria-hidden="true" />
            </span>
            <span className="exames-cli__texto">
              <span className="exames-cli__etapa-nome">{etapa.nome}</span>
              <span
                className="exames-cli__quem"
                data-parado={parado || undefined}
                title={feita ? textoDoOk(etapa) : undefined}
              >
                {feita && etapa.concluida_em
                  ? `ok em ${carimbo(etapa.concluida_em)}, por ${etapa.concluida_por?.nome ?? 'alguém que não está mais na equipe'}`
                  : ehAtual && desde
                    ? `esperando desde ${carimbo(desde).slice(0, 5)}, ${haDias(diasDesde(desde))}`
                    : !aberto
                      ? 'não chegou a acontecer'
                      : 'aguardando'}
              </span>
              {podeEditar && aoMarcar && ehAtual && (
                <span>
                  <Botao
                    miudo
                    variante="primario"
                    desabilitado={ocupado}
                    motivo="Salvando…"
                    aria-label={`Dar ok em ${etapa.nome}`}
                    onClick={() => aoMarcar(etapa.id, true)}
                  >
                    Dar ok
                  </Botao>
                </span>
              )}
              {podeEditar && aoMarcar && etapa.id === ultima?.id && (
                <span>
                  <Botao
                    miudo
                    variante="fantasma"
                    desabilitado={ocupado}
                    motivo="Salvando…"
                    aria-label={`Desfazer o ok de ${etapa.nome}`}
                    onClick={() => aoMarcar(etapa.id, false)}
                  >
                    Desfazer
                  </Botao>
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Concluído ou cancelado: uma linha, e a trilha só a pedido. */
function PedidoFechado({ exame }: { exame: ExameDoPaciente }): ReactNode {
  const [aberto, definirAberto] = useState(false);
  const cancelado = exame.cancelado_em !== null;
  const ultima = ultimaFeita(exame);
  const resumo = cancelado
    ? `cancelado em ${instanteCurto(exame.cancelado_em!)} · fica na lista e não muda mais`
    : [
        `${exame.etapas.length} de ${exame.etapas.length} etapas`,
        ultima?.concluida_em ? `última em ${carimbo(ultima.concluida_em)}` : null,
        ultima?.concluida_por?.nome ?? null,
      ]
        .filter((parte): parte is string => parte !== null)
        .join(' · ');

  return (
    <li className="exames-cli__fechado" data-cancelado={cancelado || undefined}>
      <div className="exames-cli__cabeca">
        <h4 className="exames-cli__nome">{exame.exame}</h4>
        {cancelado ? <Selo tom="erro">Cancelado</Selo> : <Selo tom="ok">Concluído</Selo>}
        <span className="exames-cli__detalhe">{resumo}</span>
        <span className="exames-cli__menu">
          <Botao
            miudo
            variante="fantasma"
            aria-expanded={aberto}
            aria-label={`${aberto ? 'Esconder' : 'Ver'} as etapas de ${exame.exame}`}
            onClick={() => definirAberto(!aberto)}
          >
            {aberto ? 'Esconder etapas' : 'Ver etapas'}
          </Botao>
        </span>
      </div>
      {aberto && (
        <>
          <DetalheDoPedido exame={exame} />
          <Trilha exame={exame} podeEditar={false} ocupado={false} desde={null} />
        </>
      )}
    </li>
  );
}

function CancelarPedido({
  clienteId,
  exame,
  aoFechar,
  aoCancelado,
}: {
  clienteId: string;
  exame: ExameDoPaciente;
  aoFechar: () => void;
  aoCancelado: () => void;
}): ReactNode {
  const api = useApi();
  const cancelar = useMutation({
    mutationFn: () =>
      api!.cancelarExameDoPaciente({ caminho: { id: clienteId, pedidoId: exame.id } }),
    onSuccess: () => {
      aoFechar();
      aoCancelado();
    },
  });
  return (
    <Dialogo
      titulo={`Cancelar o pedido de ${exame.exame}?`}
      largura="estreita"
      consequencia
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Voltar
          </Botao>
          <Botao
            variante="perigo"
            desabilitado={cancelar.isPending}
            motivo="Cancelando…"
            onClick={() => cancelar.mutate()}
          >
            {cancelar.isPending ? 'Cancelando…' : 'Cancelar pedido'}
          </Botao>
        </>
      }
    >
      <p>O pedido fica na lista, como cancelado, e não muda mais. Não dá para desfazer.</p>
      {cancelar.isError && (
        <p className="exames-cli__erro" role="alert">
          {recado(cancelar.error, 'Não foi possível cancelar o pedido.')}
        </p>
      )}
    </Dialogo>
  );
}

function CadastroDoExame({
  clienteId,
  aoFechar,
}: {
  clienteId: string;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const catalogo = useEtapasDaClinica(true);
  const [formulario, definirFormulario] = useState<FormularioDoExame>(FORMULARIO_VAZIO);
  const [etapas, definirEtapas] = useState<EtapaNovaDoExame[] | null>(null);
  // As sugeridas entram quando a lista da clínica chega; depois, é a pessoa que mexe.
  const lista = etapas ?? etapasSugeridas(catalogo.data?.itens ?? []);
  // Os campos personalizados do pedido (#568): os obrigatórios entram no cadastro.
  const camposDoPedido = useCamposPersonalizados('pedido_de_exame');
  const [mudancaDosCampos, definirMudancaDosCampos] = useState<MudancaDeCampos>({});
  const faltando = obrigatoriosVazios(camposDoPedido.data?.itens ?? [], {}, mudancaDosCampos);

  const criar = useMutation({
    mutationFn: () =>
      api!.criarExameDoPaciente({
        caminho: { id: clienteId },
        corpo: {
          ...camposDoCorpo(formulario),
          etapas: lista,
          ...(Object.keys(mudancaDosCampos).length > 0 ? { campos: mudancaDosCampos } : {}),
        },
        // Chave por tentativa: o segundo clique não cria o pedido duas vezes.
        idempotencyKey: globalThis.crypto?.randomUUID?.() ?? `exame-${Date.now()}-${Math.random()}`,
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.examesDoPaciente(clienteId) });
      aoFechar();
    },
  });

  return (
    <Dialogo
      titulo="Novo pedido de exame"
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            type="submit"
            form="exame-novo"
            desabilitado={formulario.exame.trim() === '' || faltando.length > 0 || criar.isPending}
            motivo={
              criar.isPending
                ? 'Cadastrando…'
                : formulario.exame.trim() === ''
                  ? 'Diga qual é o exame.'
                  : `Preencha ${faltando.map((campo) => campo.nome).join(', ')}.`
            }
          >
            {criar.isPending ? 'Cadastrando…' : 'Cadastrar pedido'}
          </Botao>
        </>
      }
    >
      <form
        id="exame-novo"
        className="exames-cli__pedido-novo"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (formulario.exame.trim() !== '') criar.mutate();
        }}
      >
        <CamposDoExame prefixo="exame-novo" formulario={formulario} aoMudar={definirFormulario} />
        <CamposEditaveis
          id="exame-novo-campo"
          campos={camposDoPedido.data?.itens ?? []}
          valores={{}}
          mudanca={mudancaDosCampos}
          aoMudar={definirMudancaDosCampos}
        />
        <ListaDeEtapas
          prefixo="exame-novo-etapas"
          etapas={lista}
          jaFeitas={0}
          catalogo={catalogo.data?.itens ?? []}
          aoMudar={definirEtapas}
        />
      </form>
      {criar.isError && (
        <p className="exames-cli__erro" role="alert">
          {recado(criar.error, 'Não foi possível cadastrar o exame.')}
        </p>
      )}
    </Dialogo>
  );
}

function DadosDoExame({
  prefixo,
  inicial,
  valores,
  aoSalvar,
  aoSalvo,
  aoCancelar,
}: {
  prefixo: string;
  inicial: FormularioDoExame;
  /** Os campos personalizados como estão gravados (#568). */
  valores: ValoresDeCampos;
  aoSalvar: (formulario: FormularioDoExame, campos: MudancaDeCampos) => Promise<unknown>;
  aoSalvo: () => void;
  aoCancelar: () => void;
}): ReactNode {
  const [formulario, definirFormulario] = useState(inicial);
  const camposDoPedido = useCamposPersonalizados('pedido_de_exame');
  const [mudancaDosCampos, definirMudancaDosCampos] = useState<MudancaDeCampos>({});
  const salvar = useMutation({
    mutationFn: () => aoSalvar(formulario, mudancaDosCampos),
    onSuccess: aoSalvo,
  });
  return (
    <form
      className="exames-cli__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (formulario.exame.trim() !== '') salvar.mutate();
      }}
    >
      <CamposDoExame prefixo={prefixo} formulario={formulario} aoMudar={definirFormulario} />
      <CamposEditaveis
        id={`${prefixo}-campo`}
        campos={camposDoPedido.data?.itens ?? []}
        valores={valores}
        mudanca={mudancaDosCampos}
        aoMudar={definirMudancaDosCampos}
      />
      {salvar.isError && (
        <p className="exames-cli__erro" role="alert">
          {recado(salvar.error, 'Não foi possível salvar o pedido.')}
        </p>
      )}
      <div className="exames-cli__acoes">
        <Botao variante="fantasma" type="button" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao
          variante="primario"
          type="submit"
          desabilitado={formulario.exame.trim() === '' || salvar.isPending}
          motivo={salvar.isPending ? 'Salvando…' : 'Diga qual é o exame.'}
        >
          Salvar
        </Botao>
      </div>
    </form>
  );
}

function CamposDoExame({
  prefixo,
  formulario,
  aoMudar,
}: {
  prefixo: string;
  formulario: FormularioDoExame;
  aoMudar: (formulario: FormularioDoExame) => void;
}): ReactNode {
  const mudar = <C extends keyof FormularioDoExame>(campo: C, valor: FormularioDoExame[C]) =>
    aoMudar({ ...formulario, [campo]: valor });
  return (
    <fieldset className="exames-cli__grupo">
      <legend className="exames-cli__versal">Dados do exame</legend>
      <div className="exames-cli__largo">
        <Campo
          id={`${prefixo}-exame`}
          rotulo="Exame"
          type="text"
          maxLength={200}
          value={formulario.exame}
          onChange={(e) => mudar('exame', e.currentTarget.value)}
        />
      </div>
      <Campo
        id={`${prefixo}-codigo`}
        rotulo="Código do exame"
        type="text"
        maxLength={60}
        ajuda="Opcional. O código no SISAC."
        value={formulario.codigoDoExame}
        onChange={(e) => mudar('codigoDoExame', e.currentTarget.value)}
      />
      <Campo
        id={`${prefixo}-pedido`}
        rotulo="Código do pedido"
        type="text"
        maxLength={60}
        ajuda="Opcional. O número do pedido no SISAC."
        value={formulario.codigoDoPedido}
        onChange={(e) => mudar('codigoDoPedido', e.currentTarget.value)}
      />
      <Campo
        id={`${prefixo}-laboratorio`}
        rotulo="Laboratório"
        type="text"
        maxLength={120}
        value={formulario.laboratorio}
        onChange={(e) => mudar('laboratorio', e.currentTarget.value)}
      />
      <Campo
        id={`${prefixo}-solicitante`}
        rotulo="Quem pediu"
        type="text"
        maxLength={120}
        value={formulario.solicitante}
        onChange={(e) => mudar('solicitante', e.currentTarget.value)}
      />
      <div className="exames-cli__largo">
        <CampoArea
          id={`${prefixo}-observacao`}
          rotulo="Observação"
          maxLength={2000}
          rows={2}
          value={formulario.observacao}
          onChange={(e) => mudar('observacao', e.currentTarget.value)}
        />
      </div>
      <label className="exames-cli__marca">
        <input
          type="checkbox"
          checked={formulario.urgente}
          onChange={(e) => mudar('urgente', e.currentTarget.checked)}
        />
        Urgente
      </label>
    </fieldset>
  );
}

function EdicaoDasEtapas({
  prefixo,
  inicial,
  jaFeitas,
  aoSalvar,
  aoSalvo,
  aoCancelar,
}: {
  prefixo: string;
  inicial: EtapaNovaDoExame[];
  jaFeitas: number;
  aoSalvar: (etapas: EtapaNovaDoExame[]) => Promise<unknown>;
  aoSalvo: () => void;
  aoCancelar: () => void;
}): ReactNode {
  const catalogo = useEtapasDaClinica(true);
  const [etapas, definirEtapas] = useState(inicial);
  const salvar = useMutation({ mutationFn: () => aoSalvar(etapas), onSuccess: aoSalvo });
  return (
    <form
      className="exames-cli__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        salvar.mutate();
      }}
    >
      {jaFeitas > 0 && (
        <p className="exames-cli__nota">
          {jaFeitas === 1
            ? 'A etapa que já tem ok fica'
            : `As ${jaFeitas} etapas que já têm ok ficam`}{' '}
          no começo, como aconteceram. Aqui se mexe só nas que faltam.
        </p>
      )}
      <ListaDeEtapas
        prefixo={prefixo}
        etapas={etapas}
        jaFeitas={jaFeitas}
        catalogo={catalogo.data?.itens ?? []}
        aoMudar={definirEtapas}
      />
      {salvar.isError && (
        <p className="exames-cli__erro" role="alert">
          {recado(salvar.error, 'Não foi possível salvar as etapas.')}
        </p>
      )}
      <div className="exames-cli__acoes">
        <Botao variante="fantasma" type="button" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao variante="primario" type="submit" desabilitado={salvar.isPending} motivo="Salvando…">
          Salvar etapas
        </Botao>
      </div>
    </form>
  );
}

/** A lista de etapas sendo montada: mover, tirar, e acrescentar da clínica ou escrita. */
function ListaDeEtapas({
  prefixo,
  etapas,
  jaFeitas,
  catalogo,
  aoMudar,
}: {
  prefixo: string;
  etapas: readonly EtapaNovaDoExame[];
  jaFeitas: number;
  catalogo: readonly EtapaDaClinica[];
  aoMudar: (etapas: EtapaNovaDoExame[]) => void;
}): ReactNode {
  const [escrita, definirEscrita] = useState('');
  const total = etapas.length + jaFeitas;
  const cheia = total >= LIMITE_DE_ETAPAS;
  const usadas = new Set(etapas.map((etapa) => etapa.etapa_da_clinica_id));
  const paraAcrescentar = catalogo.filter((etapa) => etapa.ativa && !usadas.has(etapa.id));

  const acrescentar = (etapa: EtapaNovaDoExame) => aoMudar([...etapas, etapa]);

  return (
    <fieldset className="exames-cli__grupo exames-cli__grupo--etapas">
      <legend className="exames-cli__versal">
        Etapas, na ordem · {total} de {LIMITE_DE_ETAPAS}
      </legend>
      {etapas.length === 0 ? (
        <p className="exames-cli__nota">Nenhuma etapa ainda.</p>
      ) : (
        <ol className="exames-cli__montagem">
          {etapas.map((etapa, indice) => (
            <li
              key={`${indice}-${etapa.nome}`}
              className="exames-cli__montada"
              data-so-deste={etapa.etapa_da_clinica_id === null || undefined}
            >
              <span className="exames-cli__disco" aria-hidden="true">
                {jaFeitas + indice + 1}
              </span>
              <span className="exames-cli__montada-nome">
                {etapa.nome}
                {etapa.etapa_da_clinica_id === null && <Chip>só deste pedido</Chip>}
              </span>
              <span className="exames-cli__botoes">
                <Botao
                  miudo
                  icone
                  variante="fantasma"
                  rotulo={`Subir ${etapa.nome}`}
                  desabilitado={indice === 0}
                  motivo="Já é a primeira."
                  onClick={() => aoMudar(mover(etapas, indice, -1))}
                >
                  <span className="exames-cli__sobe">
                    <Icone nome="abaixo" />
                  </span>
                </Botao>
                <Botao
                  miudo
                  icone
                  variante="fantasma"
                  rotulo={`Descer ${etapa.nome}`}
                  desabilitado={indice === etapas.length - 1}
                  motivo="Já é a última."
                  onClick={() => aoMudar(mover(etapas, indice, 1))}
                >
                  <Icone nome="abaixo" />
                </Botao>
                <Botao
                  miudo
                  icone
                  variante="fantasma"
                  rotulo={`Tirar ${etapa.nome}`}
                  onClick={() => aoMudar(etapas.filter((_, i) => i !== indice))}
                >
                  <Icone nome="fechar" />
                </Botao>
              </span>
            </li>
          ))}
        </ol>
      )}

      {paraAcrescentar.length > 0 && (
        <div className="exames-cli__da-clinica">
          <span className="exames-cli__rotulo">Acrescentar da lista da clínica</span>
          <span className="exames-cli__sugestoes">
            {paraAcrescentar.map((etapa) => (
              <Botao
                key={etapa.id}
                miudo
                variante="secundario"
                aria-label={`Acrescentar ${etapa.nome}`}
                desabilitado={cheia}
                motivo={`Até ${LIMITE_DE_ETAPAS} etapas.`}
                onClick={() => acrescentar({ nome: etapa.nome, etapa_da_clinica_id: etapa.id })}
              >
                <Icone nome="criar" />
                {etapa.nome}
              </Botao>
            ))}
          </span>
        </div>
      )}
      <div className="exames-cli__acrescentar">
        <Campo
          id={`${prefixo}-escrita`}
          rotulo="Ou escreva uma etapa só deste pedido"
          type="text"
          maxLength={80}
          value={escrita}
          onChange={(e) => definirEscrita(e.currentTarget.value)}
        />
        <Botao
          variante="secundario"
          desabilitado={escrita.trim() === '' || cheia}
          motivo={cheia ? `Até ${LIMITE_DE_ETAPAS} etapas.` : 'Escreva o nome da etapa.'}
          onClick={() => {
            acrescentar({ nome: escrita.trim(), etapa_da_clinica_id: null });
            definirEscrita('');
          }}
        >
          Acrescentar
        </Botao>
      </div>
      <p className="exames-cli__nota">O ok segue esta ordem.</p>
    </fieldset>
  );
}
